'use client'

import SearchPicker from '@trustoo/components/searchPicker/SearchPicker'
import { useSearchPickerContext } from '@trustoo/components/searchPicker/SearchPickerContext'
import ListKeyboardNav from '@trustoo/components/ui/ListKeyboardNav'
import { Fragment, ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

interface SearchPickerOptionsListProps {
    /** Options rendered before the fetched options; part of the list keyboard navigation. */
    prependedOptions?: ReactNode
    /** Content rendered after the list (e.g. a no results message); not part of keyboard navigation. */
    listFooterContent?: ReactNode
    /** Additional CSS class names for the list container. */
    className?: string
}

/**
 * SearchPicker.OptionsList - Options List Renderer with Keyboard Navigation
 *
 * **Role in Compound Structure:**
 * This component renders the list of searchable options with keyboard navigation support.
 * It uses ListKeyboardNav for keyboard interactions and renders each option using
 * SearchPicker.Option.
 *
 * **Responsibilities:**
 * - Renders the list of options from context
 * - Provides keyboard navigation via ListKeyboardNav
 * - Handles option selection and closes dropdown on select
 * - Supports prepending extra keyboard-navigable options (e.g., a geolocation option)
 * - Supports adding content below the list (e.g., a no-results message)
 * - Applies scrollable container with thin scrollbar styling
 */
const SearchPickerOptionsList = ({
    prependedOptions,
    listFooterContent,
    className,
}: SearchPickerOptionsListProps) => {
    const { options, closePicker } = useSearchPickerContext()

    return (
        <div className={twMerge('h-full overflow-y-auto thin-scrollbar flex flex-col', className)}>
            <ListKeyboardNav role="listbox" className="contents">
                {prependedOptions}
                {options.map((option, index) => {
                    // Render a heading only when this option starts a new consecutive group.
                    const showGroupHeading =
                        !!option.group && option.group !== options[index - 1]?.group
                    return (
                        <Fragment key={option.id}>
                            {showGroupHeading && (
                                // Non-focusable, so ListKeyboardNav skips it while arrowing.
                                // role="presentation" marks it as a non-structural child of the
                                // listbox (keeps it valid) without hiding its text from screen readers.
                                <div
                                    role="presentation"
                                    className="px-4 py-2 text-typography-text-md font-bold bg-[#eff8ff]">
                                    {option.group}
                                </div>
                            )}
                            <SearchPicker.Option
                                onClick={() => closePicker({ selectOption: option })}>
                                {option.content}
                            </SearchPicker.Option>
                        </Fragment>
                    )
                })}
            </ListKeyboardNav>
            {listFooterContent}
        </div>
    )
}

export default SearchPickerOptionsList
